import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './PlansScreen.css';
import api from '../../api';

import backIcon from '../../assets/backspace-icon.png';
import lockedIcon from '../../assets/locked-icon.png';
import unlockedIcon from '../../assets/unlocked-icon.png';

import PlanPaymentDrawer from '../../components/popups/PlanPaymentDrawer';
import QuickTopUpPopup from '../../components/popups/QuickTopUpPopup';
import SkeletonPlanCard from '../../components/skeleton/SkeletonPlanCard';

function deriveNetworkFromHost() {
  const host = (window.location.hostname || '').toLowerCase();
  if (host.includes('testnet')) return 'testnet';
  return 'mainnet';
}

function isTopUpRequiredError(e) {
  const code = e?.response?.data?.code;
  const action = e?.response?.data?.action;
  const backendMsg =
    e?.response?.data?.error ||
    e?.response?.data?.message ||
    '';

  const lowerBackendMsg = String(backendMsg || '').toLowerCase();

  return (
    code === 'INSUFFICIENT_PI' ||
    code === 'WALLET_NOT_FOUND' ||
    code === 'INSUFFICIENT_BALANCE' ||
    code === 'WALLET_INSUFFICIENT_BALANCE' ||
    action === 'TOP_UP_REQUIRED' ||
    lowerBackendMsg.includes('insufficient') ||
    lowerBackendMsg.includes('not enough pi') ||
    lowerBackendMsg.includes('no active barterfi wallet') ||
    lowerBackendMsg.includes('wallet not found') ||
    lowerBackendMsg.includes('balance')
  );
}

export default function PlansScreen() {
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [myPlan, setMyPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPlanId, setBusyPlanId] = useState(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [payOpen, setPayOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [myPlanRecord, setMyPlanRecord] = useState(null);

  const [showQuickTopUp, setShowQuickTopUp] = useState(false);
  const quickTopUpTimerRef = useRef(null);

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('barterfi_user')) || {};
    } catch {
      return {};
    }
  }, []);

  const network = useMemo(() => deriveNetworkFromHost(), []);

  const showError = useCallback((msg) => {
    setSuccess('');
    setError(msg);
    window.clearTimeout(showError._t);
    showError._t = window.setTimeout(() => setError(''), 2500);
  }, []);

  const showSuccess = useCallback((msg) => {
    setError('');
    setSuccess(msg);
    window.clearTimeout(showSuccess._t);
    showSuccess._t = window.setTimeout(() => setSuccess(''), 4000);
  }, []);

  const openQuickTopUpAfterDelay = useCallback(() => {
    if (quickTopUpTimerRef.current) {
      clearTimeout(quickTopUpTimerRef.current);
    }

    quickTopUpTimerRef.current = window.setTimeout(() => {
      setShowQuickTopUp(true);
    }, 2500);
  }, []);

  useEffect(() => {
    return () => {
      if (quickTopUpTimerRef.current) {
        clearTimeout(quickTopUpTimerRef.current);
      }
    };
  }, []);

  const getContentBoostLabel = (planId, limits = {}) => {
    if (planId === 'FREE') return 'N/A';
    const vb = limits?.visibilityBoost || {};
    if (!vb?.enabled) return 'N/A';

    const mult = Number(vb?.multiplier);
    if (!Number.isFinite(mult) || mult <= 1) return 'N/A';

    const pct = Math.round((mult - 1) * 100);
    return `+${pct}%`;
  };

  function formatExpiry(dateLike) {
    if (!dateLike) return '';
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(d);
  }

  const YnIcon = ({ ok }) => (
    <img
      src={ok ? unlockedIcon : lockedIcon}
      alt={ok ? 'Unlocked' : 'Locked'}
      className="plan-yn-icon"
      draggable="false"
    />
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);

      const [plansRes, meRes] = await Promise.all([
        api.get('/plans'),
        api.get('/plans/me'),
      ]);

      setPlans(plansRes.data?.plans || []);
      setMyPlan(meRes.data?.plan || null);
      setMyPlanRecord(meRes.data?.activeRecord || null);
    } catch (e) {
      console.error('❌ PlansScreen load error:', e);
      showError(e?.response?.data?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    setError('');
    setSuccess('');
    load();
  }, [load]);

  const orderedPlans = useMemo(() => {
    const order = ['FREE', 'BASIC', 'PREMIUM', 'PREMIUM_PLUS'];
    const byId = new Map((plans || []).map((p) => [p.id, p]));
    return order.map((id) => byId.get(id)).filter(Boolean);
  }, [plans]);

  const handlePurchase = useCallback(
    async (planId, planLabel) => {
      try {
        setBusyPlanId(planId);
        setError('');
        setSuccess('');

        await api.post('/plans/purchase', { planId, network });

        await load();
        showSuccess(`${planLabel || 'Plan'} Activated 🎉`);
      } catch (e) {
        console.error('❌ handlePurchase error:', e);

        if (isTopUpRequiredError(e)) {
          showError('Insufficient Pi');
          openQuickTopUpAfterDelay();
          return;
        }

        showError(e?.response?.data?.message || 'Purchase failed');
        throw e;
      } finally {
        setBusyPlanId(null);
      }
    },
    [load, showError, showSuccess, network, openQuickTopUpAfterDelay]
  );

  const handleRedeemCode = useCallback(
    async (code) => {
      const cleaned = String(code || '').trim();
      if (!cleaned) {
        showError('Enter a code');
        return;
      }

      try {
        setBusyPlanId('REDEEM_CODE');
        setError('');
        setSuccess('');

        await api.post('/plans/redeem-code', { code: cleaned });

        await load();
        showSuccess('Code redeemed ✅');
        setPayOpen(false);
      } catch (e) {
        console.error('❌ handleRedeemCode error:', e);
        showError(e?.response?.data?.message || 'Invalid or used code');
        throw e;
      } finally {
        setBusyPlanId(null);
      }
    },
    [load, showError, showSuccess]
  );

  return (
    <div className="plans-screen">
      {error && <div className="plans-banner error">{error}</div>}
      {success && <div className="plans-banner success">{success}</div>}

      <div className="plans-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="plans-back"
          onClick={() => navigate(-1)}
        />
        <div className="plans-title">Plans</div>
        <div className="plans-topbar-spacer" />
      </div>

      <div className={`plans-sub ${error || success ? 'has-banner' : ''}`}>
        {myPlan?.label && (
          <>
            <div className="plans-subline">
              Current: <span className="plans-pill strong">{myPlan.label}</span>
            </div>

            {myPlan?.id !== 'FREE' && myPlanRecord?.expiresAt && (
              <div className="plans-subline muted">
                Expires on:{' '}
                <span className="plans-pill">
                  {formatExpiry(myPlanRecord.expiresAt)}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {loading ? (
        <SkeletonPlanCard count={4} />
      ) : (
        <div className="plans-row">
          {orderedPlans.map((p) => {
            const isCurrent = myPlan?.id === p.id;
            const limits = p?.limits || {};

            const hasCreatorAnalytics =
              p.id === 'PREMIUM' || p.id === 'PREMIUM_PLUS';
            const canEditPost = p.id !== 'FREE';

            const hasFeedSpotlight = p.id === 'PREMIUM_PLUS';

            const boostLabel = getContentBoostLabel(p.id, limits);
            const showBoostText = p.id !== 'FREE' && boostLabel !== 'N/A';

            return (
              <div key={p.id} className="plan-card-wrap" data-plan-card="1">
                <div className={`plan-card-inner ${isCurrent ? 'is-current' : ''}`}>
                  <div className="plan-head">
                    <div className="plan-name">{p.label}</div>
                    <div className="plan-price">
                      {p.pricePi > 0 ? `${p.pricePi} Pi` : 'Free'}
                    </div>
                  </div>

                  <div className="plan-meta">
                    <div className="plan-chip">
                      {p.durationDays ? `${p.durationDays} days` : 'No expiry'}
                    </div>
                  </div>

                  <div className="plan-groups">
                    <div className="plan-group">
                      <div className="plan-group-title">Enhancements</div>
                      <div className="plan-group-list">
                        <div className="plan-group-item">
                          <span className="k">Content Boost</span>
                          <span className="v">
                            {showBoostText ? boostLabel : <YnIcon ok={false} />}
                          </span>
                        </div>

                        <div className="plan-group-item">
                          <span className="k">Feed Spotlight</span>
                          <span className="v">
                            <YnIcon ok={hasFeedSpotlight} />
                          </span>
                        </div>

                        <div className="plan-group-item">
                          <span className="k">Post text length</span>
                          <span className="v">{limits.postTextMax} chars</span>
                        </div>

                        <div className="plan-group-item">
                          <span className="k">Images per post</span>
                          <span className="v">{limits.postImagesMax}</span>
                        </div>

                        <div className="plan-group-item">
                          <span className="k">Video size</span>
                          <span className="v">{limits.postVideoMaxMB} MB</span>
                        </div>
                      </div>
                    </div>

                    <div className="plan-group">
                      <div className="plan-group-title">Creator Hub</div>
                      <div className="plan-group-list">
                        <div className="plan-group-item">
                          <span className="k">Creator Analytics</span>
                          <span className="v">
                            <YnIcon ok={hasCreatorAnalytics} />
                          </span>
                        </div>

                        <div className="plan-group-item">
                          <span className="k">Tips</span>
                          <span className="v">
                            <YnIcon ok />
                          </span>
                        </div>

                        <div className="plan-group-item">
                          <span className="k">Badges & Rewards</span>
                          <span className="v">
                            <YnIcon ok />
                          </span>
                        </div>

                        <div className="plan-group-item">
                          <span className="k">Stake Boost</span>
                          <span className="v">
                            <YnIcon ok />
                          </span>
                        </div>

                        <div className="plan-group-item">
                          <span className="k">Edit Post</span>
                          <span className="v">
                            <YnIcon ok={canEditPost} />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="plan-group">
                      <div className="plan-group-title">Marketplace</div>
                      <div className="plan-group-list">
                        <div className="plan-group-item">
                          <span className="k">Buy & Sell</span>
                          <span className="v">
                            <YnIcon ok />
                          </span>
                        </div>

                        <div className="plan-group-item">
                          <span className="k">Escrow Protection</span>
                          <span className="v">
                            <YnIcon ok />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="plan-actions">
                    {isCurrent ? (
                      <button className="plan-btn current" disabled>
                        Current plan
                      </button>
                    ) : p.pricePi <= 0 ? (
                      <button className="plan-btn" disabled>
                        Included
                      </button>
                    ) : (
                      <button
                        className="plan-btn"
                        onClick={() => {
                          setSelectedPlan(p);
                          setPayOpen(true);
                        }}
                        disabled={
                          busyPlanId === p.id ||
                          busyPlanId === 'REDEEM_CODE'
                        }
                      >
                        {busyPlanId === p.id
                          ? 'Processing…'
                          : `Upgrade to ${p.label}`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PlanPaymentDrawer
        open={payOpen}
        onClose={() => setPayOpen(false)}
        plan={selectedPlan}
        busy={
          busyPlanId === selectedPlan?.id ||
          busyPlanId === 'REDEEM_CODE'
        }
        onConfirmPi={async () => {
          if (!selectedPlan?.id) return;
          await handlePurchase(selectedPlan.id, selectedPlan.label);
          setPayOpen(false);
        }}
        onRedeemCode={handleRedeemCode}
      />

      {showQuickTopUp && (
        <QuickTopUpPopup
          open={showQuickTopUp}
          user={{ username: storedUser?.username }}
          minAmount={0.1}
          title="Insufficient Pi"
          subtitle="Top up your BarterFi wallet to upgrade your plan and unlock more creator features."
          onClose={() => setShowQuickTopUp(false)}
          onSuccess={async () => {
            setShowQuickTopUp(false);
            showSuccess('Wallet topped up successfully.');
            await load();
          }}
        />
      )}
    </div>
  );
}
